import { Check, ChevronDown, Plus, SearchMd, Stars02 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import type { Key } from "react-aria";
import { useCandidateLists } from "@/api/candidate-lists";
import type { CandidateClassification } from "@/api/candidates";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { cx } from "@/utils/cx";

type ClassificationTab = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  classification?: CandidateClassification;
};

const CLASSIFICATION_TABS: ClassificationTab[] = [
  {
    id: "recommended_candidates",
    label: "Recommended Creators",
    icon: <Stars02 className="w-4 h-4" />,
  },
  { id: "__all", label: "All" },
];

type Props = {
  selected: string;
  setSelected: (selected: string) => void;
  selectedList?: string;
  setSelectedList?: (list: string) => void;
  showCustomLists?: boolean;
  showRecommendations?: boolean;
};

export const CandidateClassificationTabs = ({
  selected,
  setSelected,
  selectedList,
  setSelectedList,
  showCustomLists = false,
  showRecommendations = false,
}: Props) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const { data: lists } = useCandidateLists({ offset: 0, limit: 50 }, "", { column: "createdAt", direction: "asc" });

  useEffect(() => setSearch(""), [open]);

  const filteredLists = useMemo(() => {
    if (!lists) return [];
    const list = lists.lists.map((list) => ({ id: list.id, label: list.name }));
    if (search.length) {
      const lowered = search.toLocaleLowerCase();
      return list.filter((item) => item.label.toLocaleLowerCase().includes(lowered));
    }
    return list;
  }, [lists, search]);

  const isListSelected = selectedList && selectedList !== "__all";
  const selectedListName = isListSelected ? lists?.lists.find((l) => l.id === selectedList)?.name : undefined;
  const hasCustomLists = lists && lists.lists.length > 0;
  const showSearch = lists && lists.lists.length > 10;

  if (!lists && showCustomLists) return <div className="h-6 w-56 animate-pulse rounded bg-gray-200" />;

  return (
    <>
      <Tabs selectedKey={selected as Key} onSelectionChange={(key) => setSelected(key as string)}>
        <Tabs.List
          type="underline"
          items={CLASSIFICATION_TABS.filter((t) => t.id !== "recommended_candidates" || showRecommendations)}
        >
          {(tab) => <Tabs.Item {...tab} className="min-w-10 max-w-56" />}
        </Tabs.List>
      </Tabs>
      {showCustomLists && setSelectedList && (
        <Dropdown.Root isOpen={open} onOpenChange={(open) => setOpen(open)}>
          <Button
            iconTrailing={<ChevronDown className="h-4 w-4" />}
            className={cx(
              "p-0 pb-[11px] pr-2 pl-4 text-sm font-semibold bg-transparent text-quaternary shadow-none hover:text-brand-secondary relative before:absolute before:inset-x-0 before:bottom-0 border-b-[1px] border-solid border-secondary rounded-none hover:border-fg-brand-primary_alt delay-100 hover:border-b-2 hover:pb-[10px] transition-all focus:outline-none",
              (open || isListSelected) && "text-brand-secondary border-fg-brand-primary_alt border-b-2 pb-[10px]",
            )}
            color="tertiary"
          >
            {selectedListName ?? "Custom Lists"}
          </Button>
          <Dropdown.Popover>
            <Dropdown.Menu>
              {hasCustomLists && (
                <>
                  <Dropdown.Section className="max-h-96 overflow-auto">
                    {filteredLists.map((item) => (
                      <Dropdown.Item
                        icon={() => (
                          <Check
                            className={cx(
                              item.id !== selectedList && "opacity-0",
                              "mr-2 size-4 shrink-0 stroke-[2.25px]",
                            )}
                          />
                        )}
                        onClick={() => setSelectedList(item.id)}
                        key={item.id}
                      >
                        <span>{item.label}</span>
                      </Dropdown.Item>
                    ))}
                    {!filteredLists.length && (
                      <Dropdown.Item className="flex flex-row justify-center hover:bg-transparent">
                        <span className="text-center font-normal italic">No Results</span>
                      </Dropdown.Item>
                    )}
                  </Dropdown.Section>
                  <Dropdown.Separator />
                  <Dropdown.Section>
                    {showSearch && (
                      <Dropdown.Item>
                        <Input
                          icon={SearchMd}
                          placeholder="Search"
                          value={search}
                          onChange={setSearch}
                          onClick={(e) => e.stopPropagation()}
                          size="sm"
                          inputClassName="text-sm"
                        />
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={() => setSelectedList("__create-new")} icon={Plus}>
                      Create new list
                    </Dropdown.Item>
                  </Dropdown.Section>
                </>
              )}
              {!hasCustomLists && (
                <Dropdown.Section>
                  <Dropdown.Item onClick={() => setSelectedList("__create-new")} icon={Plus}>
                    Create new list
                  </Dropdown.Item>
                </Dropdown.Section>
              )}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      )}
      <div className="pl-4 bg-transparent border-b-[1px] border-solid border-secondary rounded-none w-full" />
    </>
  );
};

export const getClassificationFromTab = (tabId: string): CandidateClassification | undefined => {
  const tab = CLASSIFICATION_TABS.find((t) => t.id === tabId);
  return tab?.classification;
};
